// StationsChart.tsx
import React, { forwardRef, useRef, useEffect } from "react";
import ReactECharts from "echarts-for-react";
import { buildStationsOptions } from "../options/buildStationsOptions";
import { RetroChart as RetroChartModel } from "../../../models/chartData";

interface StationsChartProps {
  title: string;
  chart: RetroChartModel;
  labels: Record<string, string>;
  activeStations: Record<string, boolean>;
  onToggleStation: (stationId: string, selected?: boolean) => void;
  onDataZoom: (params: any) => void;
  onCursorMove?: (time: number | null) => void;
  dynamicLevel: number;
  colors: Record<string, string>;
  maxDate?: number;
  zoomStart?: number;
  zoomEnd?: number;
  zoomResetVersion?: number;
}

export const StationsChart = forwardRef<any, StationsChartProps>(
  (
    {
      title,
      chart,
      labels,
      activeStations,
      onToggleStation,
      onDataZoom,
      onCursorMove,
      dynamicLevel,
      colors,
      maxDate,
      zoomStart,
      zoomEnd,
      zoomResetVersion = 0,
    },
    ref,
  ) => {
    const chartRef = useRef<ReactECharts>(null);
    const prevVersionRef = useRef(zoomResetVersion);

    useEffect(() => {
      if (zoomResetVersion !== prevVersionRef.current) {
        const instance = chartRef.current?.getEchartsInstance();
        if (instance) {
          setTimeout(() => {
            instance.dispatchAction({ type: "dataZoom", start: 0, end: 100 });
          }, 0);
        }
        prevVersionRef.current = zoomResetVersion;
      }
    }, [zoomResetVersion, chart]);

    const options = buildStationsOptions({
      chart,
      labels,
      activeSeries: activeStations,
      dynamicLevel,
      colors,
      maxDate,
      zoomStart,
      zoomEnd,
    });

    const onEvents = {
      updateAxisPointer: (params: any) => {
        if (!onCursorMove) return;
        const xValue = params.axesInfo?.[0]?.value ?? null;
        onCursorMove(xValue);
      },
      dataZoom: onDataZoom,
      legendselectchanged: (params: any) => {
        const { name, selected } = params;
        const match = name.match(/\((\d+)\)$/);
        if (match && onToggleStation && selected[name] !== undefined) {
          onToggleStation(match[1], selected[name]);
        }
      },
    };

    return (
      <div className="chart-section">
        <div className="chart-title">{title}</div>
        <ReactECharts
          ref={chartRef}
          option={options}
          style={{ height: 300, width: "100%" }}
          onEvents={onEvents}
          opts={{ renderer: "canvas" }}
          notMerge={false}
          lazyUpdate={true}
        />
      </div>
    );
  },
);

StationsChart.displayName = "StationsChart";
